import { Suspense, useState, useMemo } from 'react';
import { ErrorBoundary } from '@sentry/react';
import { Button, cn } from '@simplywallst/ui-core/next';
import { FairValueHistogram, CompanyNarrativesList } from './Narratives';
import { CompanyFeed } from '@/components/CompanyFeed';
import { toCurrencyTruncated } from '@/utilities/formatting';
import { useNarratives } from '../hooks/useNarratives';
import { MarketPerformance } from './MarketPerformance';
import { CompanySummary } from './CompanySummary';
import { CompanyDetails } from './CompanyDetails';
import { MarketInsights } from './MarketInsights';
import { useCompanyStock } from '../hooks/useCompanyStock';
import { NarrativeChart } from './NarrativeChart';
import { PriceChart as DeprecatedPriceChart } from './DeprecatedPriceChart/PriceChart';
import { Competitors as CompanyCompetitors } from './Competitors';
import {
  ROUTE_NARRATIVE_COMMUNITY,
  ROUTE_PUBLIC_NARRATIVE_CREATE,
} from '@/constants/routes';
import { getPath } from '@/router/utils';
import { getCompanyUrlSegments } from '@/pages/Narrative/utils/getNarrativeUrl';
import { getReadableCountry } from '@/utilities/markets';
import { LoadingSpinner } from '@simplywallst/ui-core';
import { SubHeading } from './SubHeading';
import { useNarrativeChart } from './NarrativeChart/hooks/useNarrativeChart';
import { TouchSlider } from './NarrativeChart/components/TouchSlider';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { NarrativeCardIsomorphic } from './NarrativeChart/components/NarrativeCard';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import { X_PADDING } from '@/pages/StockPage/components/NarrativeChart/components/TouchSlider/utils';
import { Loader } from '@/components/ScrollLoader/styled';

const BackgroundGradient = ({
  isHidden = false,
  color,
}: {
  isHidden?: boolean;
  color: string;
}) => (
  <div
    className={cn(
      'pointer-events-none absolute left-0 top-0 aspect-1 w-3/4 select-none bg-[linear-gradient(140deg,var(--gradient-color)_0%,transparent_40%)] opacity-[.04] transition-opacity delay-700 duration-1000 ease-out',
      isHidden && 'opacity-0'
    )}
    style={{ '--gradient-color': color } as React.CSSProperties}
  />
);

const Section = ({
  children,
  className,
  isPadded,
}: {
  children: React.ReactNode;
  className?: string;
  isPadded?: boolean;
}) => (
  <section>
    <div
      className={cn(
        'flex flex-col gap-3.5 rounded-lg bg-surface-1 shadow-sm',
        isPadded ? 'p-5' : 'py-5',
        className
      )}
    >
      {children}
    </div>
  </section>
);

Section.Padding = function Padding({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'gap-inherit px-5 [display:inherit] [flex-direction:inherit]',
        className
      )}
    >
      {children}
    </div>
  );
};

const splitCurrencyAtFirstNumber = (currency: string) => {
  // Match from the first digit (optionally negative, optionally decimal) to the end
  const match = currency.match(/[-]?\d*\.?\d+$/);
  if (!match) return currency;
  return match[0];
};

/**
 * @deprecated The new narrative chart is going to replace this component.
 */
export const DeprecatedChart = () => {
  const company = useCompanyStock();

  if (!company.canonicalUrl) return;

  return (
    <Section className="relative overflow-hidden bg-surface-2 pb-0" isPadded>
      <SubHeading>{company.tickerSymbol} Share Price Performance</SubHeading>
      <DeprecatedPriceChart
        lookup={company.canonicalUrl}
        currencyISO={company.listingCurrencyISO ?? 'USD'}
      >
        {(chartData) => {
          const data = {
            lastPrice:
              company.listingCurrencyISO &&
              toCurrencyTruncated(
                chartData.lastPrice ?? 0,
                2,
                company.listingCurrencyISO
              ),
            priceChange:
              company.listingCurrencyISO &&
              splitCurrencyAtFirstNumber(
                toCurrencyTruncated(
                  chartData.priceChange,
                  2,
                  company.listingCurrencyISO
                )
              ),
            percentageChange:
              ((chartData.lastPrice - chartData.firstPrice) /
                chartData.firstPrice) *
              100,
          };
          return (
            <div>
              {data.lastPrice && (
                <>
                  <span className="text-2xl">{data.lastPrice}</span>
                  {' '}
                </>
              )}
              {data.priceChange && (
                <span className="text-sm text-[--chart-line-color]">
                  {chartData.isPositiveTrend ? '↑' : '↓'}
                  {data.priceChange}
                </span>
              )}
              {' '}
              <span className="text-sm text-[--chart-line-color]">
                ({data.percentageChange.toFixed(1)}%)
              </span>
              <BackgroundGradient
                isHidden={chartData.isLoading}
                color="var(--chart-line-color)"
              />
            </div>
          );
        }}
      </DeprecatedPriceChart>
    </Section>
  );
};

export const Chart = ({ showSlider = false }: { showSlider?: boolean }) => {
  const company = useCompanyStock();

  const { data, isLoading } = useNarrativeChart({
    canonicalUrl: company.canonicalUrl,
  });
  const narratives = useMemo(
    () => data?.narratives.edges.map((n) => n.node) ?? [],
    [data]
  );

  const [selectedNarrativeIndex, setSelectedNarrativeIndex] = useState(0);

  const hasNarratives = narratives.length > 0;

  const chartContent = (
    <>
      <SubHeading>{company.tickerSymbol} Share Price Performance</SubHeading>
      <div>
        <NarrativeChart
          selectedIndex={selectedNarrativeIndex}
          narratives={narratives}
          companyId={company.companyId}
          currencyISO={company.listingCurrencyISO ?? ''}
        />
      </div>
    </>
  );

  const emptyView = (
    <div
      className="flex h-full flex-col items-start gap-3.5 rounded-lg bg-surface-1 p-5 text-sm"
      style={
        { marginInline: showSlider ? X_PADDING : 0 } as React.CSSProperties
      }
    >
      <div className="text-pretty">There are no narratives available yet.</div>
      <div>
        <Button
          variant="secondary"
          size="medium"
          href={getPath({
            type: ROUTE_PUBLIC_NARRATIVE_CREATE,
            payload: { query: { company: company.companyId } },
          })}
        >
          <PlusSolid />
          Create a narrative
        </Button>
      </div>
    </div>
  );

  // Large screens
  if (!showSlider) {
    return (
      <Section className="relative grid cursor-default grid-cols-[65.5%_minmax(0,1fr)] gap-6 overflow-hidden bg-surface-2">
        <div className="ml-5 flex flex-col gap-5">{chartContent}</div>
        <div className="flex flex-col gap-5 lg:mr-5">
          <SubHeading>{company.tickerSymbol} Community Narratives</SubHeading>
          {isLoading ? (
            <div className="flex">
              <Loader className="block size-5 animate-spin self-center [&_*]:fill-softer" />
              &#8203;
            </div>
          ) : hasNarratives ? (
            <div role="list" className="grid">
              {narratives.map((n, index) => (
                <div
                  key={n.id}
                  className="group/narrativeCard relative p-2.5"
                  onMouseEnter={() =>
                    !showSlider && setSelectedNarrativeIndex(index)
                  }
                >
                  {/* Active background */}
                  <div
                    className={cn(
                      'pointer-events-none absolute inset-0 rounded-md bg-chart-03 opacity-0 transition-opacity duration-200 group-hover/narrativeCard:opacity-[7.5%]',
                      index === selectedNarrativeIndex && 'opacity-[7.5%]'
                    )}
                  />
                  <div className="relative">
                    <NarrativeCardIsomorphic narrative={n} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            emptyView
          )}
        </div>
      </Section>
    );
  }

  // Small screens
  return (
    <Section className="relative flex flex-col gap-6 overflow-hidden bg-surface-2">
      <div className={cn('flex flex-col gap-5', !showSlider && 'ml-5')}>
        <Section.Padding>{chartContent}</Section.Padding>
      </div>
      <div className="flex flex-col gap-5 lg:mr-5">
        <Section.Padding>
          <SubHeading>{company.tickerSymbol} Community Narratives</SubHeading>
        </Section.Padding>
        {isLoading ? (
          <div className="flex">
            <Loader className="block size-5 animate-spin self-center [&_*]:fill-softer" />
            &#8203;
          </div>
        ) : hasNarratives ? (
          <TouchSlider
            initialSlide={0}
            onSlideChange={setSelectedNarrativeIndex}
          >
            {narratives.map((n, index) => (
              <div
                key={n.id}
                className={cn(
                  'relative max-w-[min(350px,calc(100vw-70px))] pr-6 transition-opacity duration-200',
                  index !== selectedNarrativeIndex && 'opacity-40'
                )}
              >
                <div className="relative">
                  <NarrativeCardIsomorphic narrative={n} />
                </div>
                <div
                  className={cn(
                    'absolute inset-0 opacity-0',
                    // Avoid accidental clicks on non-selected items
                    index === selectedNarrativeIndex && 'pointer-events-none'
                  )}
                  onClick={() => setSelectedNarrativeIndex(index)}
                />
              </div>
            ))}
          </TouchSlider>
        ) : (
          emptyView
        )}
      </div>
    </Section>
  );
};

const NarrativesContent = ({
  hasSlider = true,
  canonicalUrl,
}: {
  hasSlider?: boolean;
  canonicalUrl: string;
}) => {
  const narratives = useNarratives({ canonicalUrl });

  return (
    <>
      {narratives.shouldShowHistogram && (
        <Section.Padding>
          <FairValueHistogram company={narratives.company} height={270} />
        </Section.Padding>
      )}
      {hasSlider ? (
        <CompanyNarrativesList
          narratives={narratives.narratives}
          classNameList="[&>li]:px-5 [&>li:not(:first-child)]:-ml-10"
        />
      ) : (
        <Section.Padding>
          <CompanyNarrativesList
            narratives={narratives.narratives}
            hasSlider={false}
            classNameList="sm:grid-cols-[repeat(auto-fill,minmax(305px,1fr))]"
          />
        </Section.Padding>
      )}
    </>
  );
};

export const Narratives = ({ hasSlider = true }: { hasSlider?: boolean }) => {
  const company = useCompanyStock();

  if (!company.canonicalUrl) return;

  // TODO: Fix this, constructing the community url from the company profile url is a bit silly
  const { country, industry, exchangeTicker, companyName } =
    getCompanyUrlSegments(company.canonicalUrl);

  // This avoids showing a confusing header when the ticker is a chinese numbered one
  const symbolIsNumber = company.tickerSymbol?.match(/^\d+$/);

  return (
    <Section className="relative overflow-hidden">
      <Section.Padding>
        <SubHeading>
          {symbolIsNumber
            ? 'Community Fair Values'
            : `${company.tickerSymbol} Community Fair Values`}
        </SubHeading>
      </Section.Padding>
      {/*
       * This ErrorBoundary hides some "on-load" errors - the suspense hooks
       * have incorrect return types and null data isn't reported while fetching.
       */}
      <ErrorBoundary>
        <Suspense
          fallback={
            <div className="grid min-h-[300px] place-items-center">
              <LoadingSpinner light />
            </div>
          }
        >
          <NarrativesContent
            hasSlider={hasSlider}
            canonicalUrl={company.canonicalUrl}
          />
        </Suspense>
      </ErrorBoundary>
      <Section.Padding>
        <div>
          <Button
            href={getPath({
              type: ROUTE_NARRATIVE_COMMUNITY,
              payload: {
                country,
                industry,
                exchange_ticker: exchangeTicker,
                companyname: companyName,
              },
            }).toLowerCase()}
            variant="secondary"
          >
            View all narratives
          </Button>
        </div>
      </Section.Padding>
      <BackgroundGradient color="var(--s-color-chart-03)" />
    </Section>
  );
};

export const News = () => {
  const company = useCompanyStock();

  return (
    <Section className="relative overflow-hidden" isPadded>
      <SubHeading>Recent {company.tickerSymbol} News &amp; Updates</SubHeading>
      <div className="@container">
        <div className="grid items-start gap-x-3 gap-y-1 @[100ch]:grid-cols-2 [&_article]:-ml-2 [&_article_h4]:text-pretty">
          <CompanyFeed
            lookup={company.canonicalUrl}
            fetchLimit={14}
            showLimit={14}
            titleElement="h3"
            isCompanyReport
          />
        </div>
      </div>
      <BackgroundGradient color="#FF9212" />
    </Section>
  );
};

export const Summary = () => {
  const company = useCompanyStock();
  return (
    <Section isPadded>
      <SubHeading isSmall className="line-clamp-2">
        About {company.tickerSymbol}
      </SubHeading>
      <Suspense
        fallback={
          <div className="grid min-h-[207px] place-items-center">
            <LoadingSpinner light />
          </div>
        }
      >
        <ErrorBoundary>
          <CompanySummary company={company} />
        </ErrorBoundary>
      </Suspense>
    </Section>
  );
};

export const Details = () => {
  const company = useCompanyStock();

  return (
    <Section isPadded>
      <SubHeading isSmall className="line-clamp-2">
        {company.name} Key Details
      </SubHeading>
      <Suspense
        fallback={
          <div className="grid min-h-[207px] place-items-center">
            <LoadingSpinner light />
          </div>
        }
      >
        <ErrorBoundary>
          <CompanyDetails
            canonicalUrl={company.canonicalUrl}
            details={company.details}
          />
        </ErrorBoundary>
      </Suspense>
    </Section>
  );
};

export const Performance = () => {
  const company = useCompanyStock();

  if (!company.countryISO) return;

  return (
    <Section isPadded>
      <SubHeading isSmall>
        {getReadableCountry(
          company.countryISO.toLowerCase() as PrimaryCountryISOElseGlobal
        )}{' '}
        Market Performance
      </SubHeading>
      <MarketPerformance market={company.countryISO} industryId={0} />
    </Section>
  );
};

export const Competitors = () => {
  const company = useCompanyStock();

  if (!company.countryISO) return;

  return (
    <Section isPadded>
      <SubHeading isSmall>{company.name} Competitors</SubHeading>
      <CompanyCompetitors />
    </Section>
  );
};

export const Insights = () => {
  return (
    <section className="lg:mb-8">
      <MarketInsights />
    </section>
  );
};
